import { useCallback, useEffect, useState } from 'react';
import { BadgeCheck, Ban, Clock, Mail, RefreshCw, Search, StickyNote, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import type { ApiError, OrderStatus } from '../../lib/api';
import { formatPrice } from '../../lib/format';
import { adminApi, type AdminOrderDetail, type AdminOrderListItem } from '../../lib/admin';

const STATUS_LABEL: Record<OrderStatus, string> = {
  CREATED: 'Vytvořena',
  AWAITING_QR: 'Čeká na platbu (QR)',
  AWAITING_CARD: 'Čeká na platbu (karta)',
  PAID: 'Zaplaceno',
  DELIVERED: 'Doručeno',
  EXPIRED: 'Vypršelo',
  CANCELLED: 'Zrušeno',
  REFUNDED: 'Vráceno',
};

function statusClasses(status: OrderStatus): string {
  switch (status) {
    case 'PAID':
    case 'DELIVERED':
      return 'bg-success/10 text-success';
    case 'AWAITING_QR':
    case 'AWAITING_CARD':
    case 'CREATED':
      return 'bg-warning/10 text-warning';
    case 'EXPIRED':
    case 'CANCELLED':
    case 'REFUNDED':
      return 'bg-error/10 text-error';
    default:
      return 'bg-paper2 text-ink-soft';
  }
}

function StatusBadge({ status }: { status: OrderStatus }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${statusClasses(status)}`}
    >
      {STATUS_LABEL[status]}
    </span>
  );
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('cs-CZ', {
    day: 'numeric',
    month: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const FILTERS: { value: OrderStatus | 'ALL'; label: string }[] = [
  { value: 'ALL', label: 'Vše' },
  { value: 'AWAITING_QR', label: 'Čeká na platbu' },
  { value: 'PAID', label: 'Zaplaceno' },
  { value: 'DELIVERED', label: 'Doručeno' },
];

/* Detail objednávky */

function OrderDetailPanel({
  order,
  onChanged,
}: {
  order: AdminOrderDetail;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);

  const run = async (label: string, fn: () => Promise<unknown>, ok: string) => {
    setBusy(label);
    try {
      await fn();
      toast.success(ok);
      onChanged();
    } catch (err) {
      toast.error('Akci se nepodařilo provést', {
        description: (err as ApiError)?.message,
      });
    } finally {
      setBusy(null);
    }
  };

  const canMarkPaid = ['CREATED', 'AWAITING_QR', 'AWAITING_CARD'].includes(order.status);
  const canCancel = canMarkPaid;
  const canResend = ['PAID', 'DELIVERED'].includes(order.status);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-serif text-xl leading-tight">{order.product_title}</h2>
          <p className="mt-0.5 text-sm text-ink-soft">{order.email}</p>
        </div>
        <StatusBadge status={order.status} />
      </div>

      <dl className="grid grid-cols-2 gap-3 text-sm">
        <div className="rounded-xl border border-line bg-paper px-3 py-2">
          <dt className="text-xs font-semibold uppercase tracking-wide text-ink-soft">Částka</dt>
          <dd className="mt-0.5 font-bold tabular-nums text-ink">
            {formatPrice(order.amount_czk)}
          </dd>
        </div>
        <div className="rounded-xl border border-line bg-paper px-3 py-2">
          <dt className="text-xs font-semibold uppercase tracking-wide text-ink-soft">
            Variabilní symbol
          </dt>
          <dd className="mt-0.5 font-mono font-semibold text-ink">{order.variable_symbol}</dd>
        </div>
        <div className="rounded-xl border border-line bg-paper px-3 py-2">
          <dt className="text-xs font-semibold uppercase tracking-wide text-ink-soft">Vytvořeno</dt>
          <dd className="mt-0.5 text-ink">{formatDateTime(order.created_at)}</dd>
        </div>
        <div className="rounded-xl border border-line bg-paper px-3 py-2">
          <dt className="text-xs font-semibold uppercase tracking-wide text-ink-soft">Zaplaceno</dt>
          <dd className="mt-0.5 text-ink">{formatDateTime(order.paid_at)}</dd>
        </div>
      </dl>

      {/* Platby */}
      <div>
        <h3 className="flex items-center gap-2 text-sm font-semibold text-ink">
          <Wallet className="h-4 w-4 text-board" strokeWidth={2} /> Platby
        </h3>
        {order.payments.length === 0 ? (
          <p className="mt-1 text-sm text-ink-soft">Zatím žádný platební pokus.</p>
        ) : (
          <ul className="mt-1.5 space-y-1.5">
            {order.payments.map((p) => (
              <li
                key={p.id}
                className="flex items-center justify-between rounded-lg border border-line bg-paper px-3 py-2 text-sm"
              >
                <span className="font-medium">{p.method === 'QR' ? 'QR převod' : 'Karta'}</span>
                <span className="text-ink-soft">
                  {p.state === 'CONFIRMED'
                    ? 'Potvrzeno'
                    : p.state === 'FAILED'
                      ? 'Selhalo'
                      : 'Čeká'}
                  {' · '}
                  {formatPrice(p.amount_czk)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Fio audit */}
      {order.fio_transactions.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-ink">Spárované bankovní platby</h3>
          <ul className="mt-1.5 space-y-1.5">
            {order.fio_transactions.map((t) => (
              <li
                key={t.id}
                className="rounded-lg border border-line bg-paper px-3 py-2 text-sm text-ink-soft"
              >
                {formatPrice(t.amount_czk)} · VS {t.vs ?? '-'} · {formatDateTime(t.received_at)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Poznámka */}
      {order.delivery_note && (
        <div className="flex items-start gap-2 rounded-xl border border-warning/30 bg-warning/5 px-3 py-2.5 text-sm">
          <StickyNote className="mt-0.5 h-4 w-4 flex-none text-warning" strokeWidth={2} />
          <p className="text-ink-soft">{order.delivery_note}</p>
        </div>
      )}

      {/* Akce */}
      <div className="flex flex-wrap gap-2 border-t border-line pt-4">
        {canMarkPaid && (
          <button
            type="button"
            disabled={busy !== null}
            onClick={() =>
              run(
                'paid',
                () =>
                  adminApi.markPaid(
                    order.id,
                    window.prompt('Poznámka k ruční platbě (nepovinné):') ?? undefined,
                  ),
                'Označeno jako zaplaceno a doručeno',
              )
            }
            className="inline-flex h-11 items-center gap-2 rounded-xl bg-board px-4 text-sm font-semibold text-chalk transition hover:bg-board-deep disabled:opacity-50"
          >
            <BadgeCheck className="h-4 w-4" strokeWidth={2} />
            Zaplaceno ručně
          </button>
        )}
        {canResend && (
          <button
            type="button"
            disabled={busy !== null}
            onClick={() =>
              run('resend', () => adminApi.resendDelivery(order.id), 'E-mail odeslán znovu')
            }
            className="inline-flex h-11 items-center gap-2 rounded-xl border border-line bg-card px-4 text-sm font-semibold text-ink transition hover:border-board disabled:opacity-50"
          >
            <Mail className="h-4 w-4" strokeWidth={2} />
            Znovu odeslat e-mail
          </button>
        )}
        {canCancel && (
          <button
            type="button"
            disabled={busy !== null}
            onClick={() =>
              run(
                'cancel',
                () =>
                  adminApi.cancelOrder(
                    order.id,
                    window.prompt('Důvod zrušení (nepovinné):') ?? undefined,
                  ),
                'Objednávka zrušena',
              )
            }
            className="inline-flex h-11 items-center gap-2 rounded-xl border border-error/40 bg-card px-4 text-sm font-semibold text-error transition hover:bg-error/5 disabled:opacity-50"
          >
            <Ban className="h-4 w-4" strokeWidth={2} />
            Zrušit
          </button>
        )}
      </div>
    </div>
  );
}

/* Stránka */

export default function AdminOrders() {
  const [filter, setFilter] = useState<OrderStatus | 'ALL'>('ALL');
  const [search, setSearch] = useState('');
  const [orders, setOrders] = useState<AdminOrderListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<AdminOrderDetail | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.listOrders({
        status: filter === 'ALL' ? undefined : filter,
        q: search.trim() || undefined,
      });
      setOrders(data);
    } catch (err) {
      toast.error('Objednávky se nepodařilo načíst', {
        description: (err as ApiError)?.message,
      });
    } finally {
      setLoading(false);
    }
  }, [filter, search]);

  useEffect(() => {
    void load();
  }, [load]);

  const openDetail = async (id: number) => {
    try {
      setSelected(await adminApi.getOrder(id));
    } catch (err) {
      toast.error('Detail se nepodařilo načíst', { description: (err as ApiError)?.message });
    }
  };

  const refreshAll = async () => {
    await load();
    if (selected) await openDetail(selected.id);
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h1 className="font-serif text-2xl">Objednávky</h1>
        <button
          type="button"
          onClick={() => void load()}
          className="inline-flex h-10 items-center gap-2 rounded-xl border border-line bg-card px-3 text-sm font-semibold text-ink transition hover:border-board"
        >
          <RefreshCw className="h-4 w-4" strokeWidth={2} />
          Obnovit
        </button>
      </div>

      {/* Filtry + hledání */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => setFilter(f.value)}
            className={`h-9 rounded-full px-3.5 text-sm font-semibold transition ${
              filter === f.value
                ? 'bg-board text-chalk'
                : 'border border-line bg-card text-ink-soft'
            }`}
          >
            {f.label}
          </button>
        ))}
        <div className="ml-auto flex h-9 min-w-[220px] items-center gap-2 rounded-full border border-line bg-card px-3">
          <Search className="h-4 w-4 flex-none text-ink-soft" strokeWidth={2} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="E-mail, VS, kód…"
            className="w-full bg-transparent text-sm outline-none"
          />
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
        {/* Seznam */}
        <div className="space-y-2">
          {loading ? (
            <div className="h-40 rounded-card bg-paper2 motion-safe:animate-pulse" />
          ) : orders.length === 0 ? (
            <p className="rounded-card border border-line bg-card p-6 text-center text-ink-soft">
              Žádné objednávky.
            </p>
          ) : (
            orders.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => void openDetail(o.id)}
                className={`flex w-full items-center gap-3 rounded-card border bg-card p-3.5 text-left shadow-card transition hover:border-board ${
                  selected?.id === o.id ? 'border-board' : 'border-line'
                }`}
              >
                <div className="min-w-0 grow">
                  <p className="truncate font-semibold text-ink">{o.product_title}</p>
                  <p className="mt-0.5 flex items-center gap-2 text-sm text-ink-soft">
                    <span className="truncate">{o.email}</span>
                    {o.has_note && (
                      <StickyNote className="h-3.5 w-3.5 flex-none text-warning" strokeWidth={2} />
                    )}
                  </p>
                  <p className="mt-0.5 flex items-center gap-1 text-xs text-ink-soft">
                    <Clock className="h-3 w-3" strokeWidth={2} />
                    {formatDateTime(o.created_at)}
                  </p>
                </div>
                <div className="flex flex-none flex-col items-end gap-1.5">
                  <span className="font-bold tabular-nums text-ink">
                    {formatPrice(o.amount_czk)}
                  </span>
                  <StatusBadge status={o.status} />
                </div>
              </button>
            ))
          )}
        </div>

        {/* Detail */}
        <div className="lg:sticky lg:top-4 lg:self-start">
          {selected ? (
            <div className="rounded-card border border-line bg-card p-5 shadow-card">
              <OrderDetailPanel order={selected} onChanged={() => void refreshAll()} />
            </div>
          ) : (
            <p className="rounded-card border border-dashed border-line bg-card/60 p-6 text-center text-sm text-ink-soft">
              Vyberte objednávku vlevo pro detail a akce.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
